import { useCallbackRef } from "./use-callback-ref.ts"

/**
 * Configuration options for the Intersection Observer.
 */
interface IntersectionHookProps {
  /** The root element to use as the viewport. Default is the browser viewport. */
  root?: Element | null
  /** Margin around the root. Can have values similar to the CSS margin property. */
  rootMargin?: string
  /** Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers. */
  threshold?: number | number[]
}

const defaultOptions: IntersectionHookProps = {
  root: null,
  rootMargin: "0px",
  threshold: 0.1,
}

/**
 * Custom hook for tracking intersection of one or more elements with a specified root.
 *
 * @param onIntersect - Callback function to be called when intersection changes
 * @param options - Configuration options for the Intersection Observer
 * @returns Array of refs to attach to the observed elements
 */
export const useIntersection = <T extends HTMLElement>({
  onIntersect,
  options = {},
}: {
  onIntersect: (entry: IntersectionObserverEntry) => void
  options?: IntersectionHookProps
}) => {
  const ref = useCallbackRef<T>((node) => {
    const observerOptions = { ...defaultOptions, ...options }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(onIntersect)
    }, observerOptions)

    observer.observe(node)

    return () => {
      observer.unobserve(node)
    }
  })

  return ref
}
